import * as React from 'react';
import * as R from 'ramda';
import Page from 'ha/modules/Page';
import { SEARCH } from 'ha/constants/pageNames';
import SomethingWentWrongPage from 'ha/pages/SomethingWentWrongPage';
import NotFoundPage from 'ha/pages/NotFound';
import MediaQuery from 'ha/helpers/MediaQuery';
import SearchPageBreadcrumbs from '../containers/SearchPageBreadcrumbs';
import SearchLayout from './SearchLayout';
import { RequiredAnalyticsFilters } from '../constants';

interface Props {
  error: boolean;
  notFound: boolean;
  isAuthenticated: boolean;
  isSuggestionBarVisible: boolean;
  cityCanonical: string;
  countryCode: string;
  pageName: string;
  searchParams: {
    kinds: string[];
  };
  location: { query: { page: string }; pathname: string };
  filtersAnalytics: {
    numberOfPartnerListings: number;
    numberOfOrganicListings: number;
    latitude: number;
    longitude: number;
    zoomLevel: number;
    listingIdResults: number[];
    endDate: string;
    startDate: string;
    priceMax: number | string;
    priceMin: number | string;
  };
  listingsAnalytics: {
    numberOfSearchResults: number;
    numberOfPartnerListings: number;
    numberOfOrganicListings: number;
    isNumberOfSearchResultsExhaustive: boolean;
    listingIdResults: number[];
    areCountersOfOrganicAndPartnerListingsExhaustive: boolean;
  };
  previewNumberOfListings?: number;
  onTrackFilterApplied: (filters: object) => void;
  getFavoriteListings: () => void;
  getSearchAlert: () => void;
}

const pickRequiredAnalytics = R.pick(Object.keys(RequiredAnalyticsFilters));
const pickMapParams = R.pick(['topLat', 'bottomLat', 'leftLng', 'rightLng']);

export default class SearchRoot extends React.Component<Props> {
  componentDidMount() {
    const { getFavoriteListings, getSearchAlert } = this.props;

    getFavoriteListings();
    getSearchAlert();
  }

  componentDidUpdate(prevProps: Props) {
    const prevFilters = pickRequiredAnalytics(prevProps.searchParams);
    const filters = pickRequiredAnalytics(this.props.searchParams);

    const prevMapParams = pickMapParams(prevProps.searchParams);
    const mapParams = pickMapParams(this.props.searchParams);

    if (
      !R.equals(prevFilters, filters) ||
      (!R.isEmpty(prevMapParams) && !R.equals(prevMapParams, mapParams))
    ) {
      this.maybeTrackFilterApplied();
    }
  }

  maybeTrackFilterApplied() {
    const { filtersAnalytics } = this.props;

    if (!R.isEmpty(filtersAnalytics)) {
      this.props.onTrackFilterApplied(filtersAnalytics);
    }
  }

  render() {
    const {
      location,
      error,
      notFound,
      isAuthenticated,
      cityCanonical,
      countryCode,
      pageName,
      listingsAnalytics,
      searchParams: { kinds },
      filtersAnalytics: { endDate, startDate },
      previewNumberOfListings,
      isSuggestionBarVisible,
    } = this.props;

    if (error) {
      return <SomethingWentWrongPage />;
    }
    if (notFound) {
      return <NotFoundPage />;
    }

    return (
      <MediaQuery>
        {({ lg }) => (
          <Page
            name={pageName}
            category={SEARCH}
            breadcrumbs={lg ? <SearchPageBreadcrumbs /> : null}
            traits={{
              ...listingsAnalytics,
              cityCanonical,
              countryCode,
            }}
            remarketingTag={{
              hrental_pagetype: 'searchresults',
              hrental_startdate: startDate,
              hrental_enddate: endDate,
              hrental_city: cityCanonical,
              hrental_country: countryCode,
              hrental_search_category: kinds, // TODO: change to categories and map to old values
            }}
            hasFooter={false}
            hideIntercom
            excludeLegacyCSSBundle={false}
          >
            <SearchLayout
              location={location}
              isSuggestionBarVisible={
                !isAuthenticated && isSuggestionBarVisible
              }
              previewNumberOfListings={previewNumberOfListings}
            />
          </Page>
        )}
      </MediaQuery>
    );
  }
}
